/**
 * JavaScript helpers to replace jQuery functionality
 * These utilities provide vanilla JS alternatives to jQuery methods
 */

/**
 * Делает первый символ строки заглавным. Пустая строка возвращается без изменений.
 */
export function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/**
 * Fade out animation helper
 */
export function fadeOut(element: HTMLElement, duration: number = 500, callback?: () => void): void {
  if (!element) return
  
  const startOpacity = parseFloat(window.getComputedStyle(element).opacity) || 1
  const startTime = performance.now()
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    element.style.opacity = String(startOpacity * (1 - progress))
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      element.style.display = 'none'
      if (callback) callback()
    }
  }
  
  requestAnimationFrame(animate)
}

/**
 * Fade in animation helper
 */
export function fadeIn(element: HTMLElement, duration: number = 500, callback?: () => void): void {
  if (!element) return
  
  element.style.display = ''
  element.style.opacity = '0'
  
  const startTime = performance.now()
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    element.style.opacity = String(progress)
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      if (callback) callback()
    }
  }
  
  requestAnimationFrame(animate)
}

/**
 * Slide toggle animation helper
 */
export function slideToggle(element: HTMLElement, duration: number = 400, callback?: () => void): void {
  if (!element) return
  
  const isHidden = element.style.display === 'none' || !element.style.display
  
  if (isHidden) {
    slideDown(element, duration, callback)
  } else {
    slideUp(element, duration, callback)
  }
}

/**
 * Slide down animation helper
 */
export function slideDown(element: HTMLElement, duration: number = 400, callback?: () => void): void {
  if (!element) return
  
  const startHeight = element.offsetHeight
  element.style.height = '0'
  element.style.display = 'block'
  element.style.overflow = 'hidden'
  
  const targetHeight = element.scrollHeight
  const startTime = performance.now()
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    const currentHeight = startHeight + (targetHeight - startHeight) * progress
    element.style.height = `${currentHeight}px`
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      element.style.height = ''
      element.style.overflow = ''
      if (callback) callback()
    }
  }
  
  requestAnimationFrame(animate)
}

/**
 * Slide up animation helper
 */
export function slideUp(element: HTMLElement, duration: number = 400, callback?: () => void): void {
  if (!element) return
  
  const startHeight = element.offsetHeight
  element.style.overflow = 'hidden'
  
  const startTime = performance.now()
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    const currentHeight = startHeight * (1 - progress)
    element.style.height = `${currentHeight}px`
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      element.style.display = 'none'
      element.style.height = ''
      element.style.overflow = ''
      if (callback) callback()
    }
  }
  
  requestAnimationFrame(animate)
}

/**
 * Smooth scroll to element
 */
export function smoothScrollTo(target: string | HTMLElement, offset: number = 0, duration: number = 800): void {
  const element = typeof target === 'string' 
    ? document.querySelector(target) as HTMLElement
    : target
  
  if (!element) return
  
  const startPosition = window.pageYOffset
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset
  const distance = targetPosition - startPosition
  const startTime = performance.now()
  
  function animate(currentTime: number) {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    // Easing function (ease-in-out)
    const ease = progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2
    
    window.scrollTo(0, startPosition + distance * ease)
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    }
  }
  
  requestAnimationFrame(animate)
}

/**
 * Add/remove class with transition
 */
export function toggleClass(element: HTMLElement, className: string, force?: boolean): void {
  if (!element) return
  
  if (force === undefined) {
    element.classList.toggle(className)
  } else {
    if (force) {
      element.classList.add(className)
    } else {
      element.classList.remove(className)
    }
  }
}

/**
 * Delay helper (returns a promise)
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Get element by selector (querySelector wrapper)
 */
export function $(selector: string): HTMLElement | null {
  return document.querySelector(selector) as HTMLElement | null
}

/**
 * Get all elements by selector (querySelectorAll wrapper)
 */
export function $$(selector: string): NodeListOf<HTMLElement> {
  return document.querySelectorAll(selector) as NodeListOf<HTMLElement>
}

/**
 * Check if element exists
 */
export function elementExists(selector: string): boolean {
  return !!document.querySelector(selector)
}

/**
 * Wait for DOM to be ready
 */
export function domReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback)
  } else {
    callback()
  }
}

/**
 * Wait for window load
 */
export function windowLoad(callback: () => void): void {
  if (document.readyState === 'complete') {
    callback()
  } else {
    window.addEventListener('load', callback)
  }
}

/**
 * Форматирует длительность урока (минуты) в MM:SS или H:MM:SS.
 * Используется для отображения в программе курса.
 */
export function formatLessonDuration(minutes: number | undefined): string {
  if (minutes == null || minutes < 0) return ''
  const m = Math.floor(minutes) % 60
  const h = Math.floor(minutes / 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  if (h > 0) {
    return `${h}:${pad(m)}:00`
  }
  return `${pad(m)}:00`
}

/**
 * Универсальная функция для группировки массива объектов по ключу или функции-критерию
 * @param items - массив элементов для группировки
 * @param keyOrFn - ключ объекта (строка) или функция, возвращающая ключ группировки
 * @returns Map с ключами групп и массивами элементов
 * 
 * @example
 * // Группировка по полю
 * groupBy(users, 'role')
 * 
 * // Группировка по функции
 * groupBy(courses, (course) => course.locale || 'unknown')
 */
export function groupBy<T, K = string | number | undefined | null>(
  items: T[],
  keyOrFn: string | ((item: T) => K)
): Map<K, T[]> {
  const groups = new Map<K, T[]>()

  if (!items || items.length === 0) {
    return groups
  }

  const getKey = typeof keyOrFn === 'string'
    ? (item: T) => (item as any)[keyOrFn] as K
    : keyOrFn

  for (const item of items) {
    const key = getKey(item)
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(item)
  }

  return groups
}

/**
 * Группирует уроки по lesson_topic (пустая строка и отсутствие темы → undefined).
 * Используется для построения секций программы курса.
 */
export function groupLessonsByTopic<T extends { lesson_topic?: string }>(
  lessons: T[],
): Map<string | undefined, T[]> {
  return groupBy(lessons, (lesson) => lesson.lesson_topic?.trim() || undefined)
}

/**
 * Сортирует уроки по lesson_order (по возрастанию).
 */
export function sortLessonsByOrder<T extends { lesson_order?: number }>(lessons: T[]): T[] {
  return [...lessons].sort((a, b) => (a.lesson_order ?? 0) - (b.lesson_order ?? 0))
}

/**
 * Форматирует overview программы курса для отображения.
 * parts — из mapCourseToCurriculumOverviewParts (infrastructure/mappers/course.mapper); t — функция i18n (useI18n().t).
 * Ключи: courseDetails.curriculum.lessonsUnit, hoursShort, minutesShort, defaultOverview.
 */
export function formatCurriculumOverview(
  parts: { lessonsAmount: number; duration: number; hours: number; minutes: number } | null,
  t: (key: string) => string,
): string {
  if (!parts) return t('courseDetails.curriculum.defaultOverview')
  if (!parts.duration) {
    return `${parts.lessonsAmount} ${t('courseDetails.curriculum.lessonsUnit')}`
  }
  return `${parts.lessonsAmount} ${t('courseDetails.curriculum.lessonsUnit')} | ${parts.hours}${t('courseDetails.curriculum.hoursShort')} ${parts.minutes}${t('courseDetails.curriculum.minutesShort')}`
}

